import React, { useState, useEffect, createContext, useContext } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Badge,
  Box,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';

// Hooks
import { usePermissions } from '../../../features/auth/hooks/usePermissions';
import { useNotificationsContext } from '../../../context/NotificationsContext';
import { VersionMenu } from './VersionMenu';
import { useNavigationStack } from '../../../context/NavigationStackContext';

// Styled Components
const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  borderRadius: theme.shape.borderRadius * 3,
  margin: theme.spacing(0, 0.75),
  padding: theme.spacing(1.5, 2.5),
  minHeight: 34,
  transition: 'background-color 0.15s ease-in-out, color 0.15s ease-in-out',
  position: 'relative',
  color: theme.palette.text.secondary,
  '& .MuiListItemIcon-root': {
    color: theme.palette.text.tertiary,
  },
  ...(active && {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.primary.main,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 6,
      bottom: 6,
      width: 3,
      borderRadius: 3,
      backgroundColor: theme.palette.primary.main,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
    '& .MuiListItemText-primary': {
      fontWeight: 600,
    },
  }),
  ...(!active && {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
  }),
}));

const SubItemButton = styled(StyledListItemButton)(({ theme }) => ({
  marginLeft: theme.spacing(5),
}));

const GroupListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'hasActiveChild',
})(({ theme, hasActiveChild }) => ({
  borderRadius: theme.shape.borderRadius * 3,
  margin: theme.spacing(0, 0.75),
  padding: theme.spacing(1.5, 2.5),
  minHeight: 34,
  transition: 'background-color 0.15s ease-in-out',
  position: 'relative',
  color: theme.palette.text.secondary,
  '& .MuiListItemIcon-root': {
    color: theme.palette.text.tertiary,
  },
  ...(hasActiveChild && {
    '& .MuiListItemText-primary': {
      fontWeight: 600,
      color: theme.palette.primary.main,
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
  }),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const SectionDivider = styled(Box)(({ theme }) => ({
  height: 1,
  backgroundColor: theme.palette.divider,
  margin: theme.spacing(2, 2.5, 1.5, 2.5),
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: 'calc(100% - 12px)',
  margin: theme.spacing(0, 0.75, 0.5, 0.75),
  padding: theme.spacing(1, 2.5),
  border: 'none',
  borderRadius: theme.shape.borderRadius * 3,
  background: 'none',
  fontFamily: theme.typography.fontFamily,
  fontSize: '11px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: theme.palette.text.tertiary,
  lineHeight: 1.2,
  textAlign: 'left',
  'button&': {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.secondary,
    },
  },
  '& .MuiSvgIcon-root': {
    marginLeft: 'auto',
    fontSize: '16px',
  },
}));

const ScrollableList = styled(List)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  '&::-webkit-scrollbar': {
    width: 4,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.divider,
    borderRadius: 2,
  },
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: 30,
  color: 'inherit',
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
  },
}));

const SubItemIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: 26,
  color: 'inherit',
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
  },
}));

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  margin: 0,
  '& .MuiListItemText-primary': {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: 1.3,
  },
}));

const NotificationBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: -2,
    top: 2,
    fontSize: '9px',
    height: 14,
    minWidth: 14,
    padding: '0 3px',
    fontWeight: 600,
  },
}));

const DrawerFooter = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2, 3, 0, 3),
  color: theme.palette.text.tertiary,
}));

const EXPANDED_STORAGE_KEY = 'orbit_nav_sections';

const loadExpanded = () => {
  try {
    return JSON.parse(localStorage.getItem(EXPANDED_STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

// Paths of all NavItems below `children`, including ones nested in NavGroups.
const collectPaths = (children) => React.Children.toArray(children).flatMap((child) => {
  if (!React.isValidElement(child)) return [];
  if (child.props.path) return [child.props.path];
  return collectPaths(child.props.children);
});

// Navigation Context
const NavigationContext = createContext();

const useNavigationContext = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('Navigation components must be used within NavigationProvider');
  }
  return context;
};

// Navigation Provider Component
const NavigationProvider = ({ children, onItemClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { unreadCount } = useNotificationsContext();
  const { clearStack } = useNavigationStack();

  const [expandedGroups, setExpandedGroups] = useState(loadExpanded);

  useEffect(() => {
    try {
      localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify(expandedGroups));
    } catch {
      // localStorage unavailable — expansion just won't persist
    }
  }, [expandedGroups]);

  const handleNavigation = (path) => {
    clearStack();
    navigate(path);
    onItemClick?.();
  };

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    
    if (location.pathname === path) {
      return true;
    }
    
    return location.pathname.startsWith(path);
  };

  const isExpanded = (groupId, defaultExpanded) => (
    expandedGroups[groupId] !== undefined ? expandedGroups[groupId] : defaultExpanded
  );

  const setGroupExpanded = (groupId, expanded) => {
    setExpandedGroups(prev => (prev[groupId] === expanded ? prev : { ...prev, [groupId]: expanded }));
  };

  const shouldShow = (permission) => {
    if (!permission) return true;
    return permissions.hasScope(permission);
  };

  const value = {
    handleNavigation,
    isActive,
    isExpanded,
    setGroupExpanded,
    shouldShow,
    unreadCount,
    location,
    permissions
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

// Tree Components
const NavSection = ({
  id,
  title,
  showDivider = true,
  collapsible = false,
  defaultExpanded = true,
  children,
}) => {
  const { shouldShow, isActive, isExpanded, setGroupExpanded } = useNavigationContext();

  const validChildren = React.Children.toArray(children).filter(child => {
    if (React.isValidElement(child) && child.props.permission) {
      return shouldShow(child.props.permission);
    }
    return true;
  });

  const hasActiveChild = collectPaths(validChildren).some(isActive);
  const expanded = !collapsible || isExpanded(id, defaultExpanded);

  // Navigating into a collapsed section (search, link, deep link) opens it,
  // but the user can still collapse it again afterwards.
  useEffect(() => {
    if (collapsible && hasActiveChild) setGroupExpanded(id, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsible, hasActiveChild, id]);

  if (validChildren.length === 0) return null;

  return (
    <>
      {showDivider && <SectionDivider />}
      {title && (collapsible ? (
        <SectionTitle
          component="button"
          type="button"
          aria-expanded={expanded}
          onClick={() => setGroupExpanded(id, !expanded)}
        >
          {title}
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </SectionTitle>
      ) : (
        <SectionTitle>{title}</SectionTitle>
      ))}
      {collapsible ? (
        <Collapse in={expanded} timeout="auto">
          {validChildren}
        </Collapse>
      ) : validChildren}
    </>
  );
};

const NavItem = ({ 
  path, 
  label, 
  icon, 
  permission, 
  badge = false,
  badgeContent,
  isSubItem = false 
}) => {
  const { handleNavigation, isActive, shouldShow, unreadCount } = useNavigationContext();
  
  if (permission && !shouldShow(permission)) {
    return null;
  }

  const active = isActive(path);
  const ButtonComponent = isSubItem ? SubItemButton : StyledListItemButton;
  const IconComponent = isSubItem ? SubItemIcon : StyledListItemIcon;

  const handleClick = (e) => {
    // Only prevent default if it's a regular click (not ctrl/cmd/middle click)
    if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
      e.preventDefault();
      handleNavigation(path);
    }
  };

  const renderIcon = () => {
    if (path === '/notifications' && unreadCount > 0) {
      return (
        <NotificationBadge badgeContent={unreadCount} color="error" max={99}>
          {icon}
        </NotificationBadge>
      );
    }
    
    if (badge && badgeContent) {
      return (
        <NotificationBadge badgeContent={badgeContent} color="error" max={99}>
          {icon}
        </NotificationBadge>
      );
    }
    
    return icon;
  };

  return (
    <ListItem disablePadding sx={{ mb: 0.125 }}>
      <ButtonComponent
        component="a"
        href={path}
        active={active}
        onClick={handleClick}
      >
        <IconComponent>{renderIcon()}</IconComponent>
        <StyledListItemText primary={label} />
      </ButtonComponent>
    </ListItem>
  );
};

const NavGroup = ({ 
  id, 
  label, 
  icon, 
  permission, 
  defaultExpanded = false,
  children 
}) => {
  const { isExpanded, setGroupExpanded, shouldShow, isActive } = useNavigationContext();
  
  if (permission && !shouldShow(permission)) {
    return null;
  }

  const validChildren = React.Children.toArray(children).filter(child => {
    if (React.isValidElement(child) && child.props.permission) {
      return shouldShow(child.props.permission);
    }
    return true;
  });

  if (validChildren.length === 0) return null;

  const expanded = isExpanded(id, defaultExpanded);

  const hasActiveChild = validChildren.some(child => {
    if (React.isValidElement(child) && child.props.path) {
      return isActive(child.props.path);
    }
    return false;
  });

  const childrenWithProps = React.Children.map(validChildren, child => {
    if (React.isValidElement(child) && child.type === NavItem) {
      return React.cloneElement(child, { isSubItem: true });
    }
    return child;
  });

  return (
    <Box>
      <ListItem disablePadding sx={{ mb: 0.125 }}>
        <GroupListItemButton
          hasActiveChild={hasActiveChild}
          onClick={() => setGroupExpanded(id, !expanded)}
        >
          <StyledListItemIcon>{icon}</StyledListItemIcon>
          <StyledListItemText primary={label} />
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </GroupListItemButton>
      </ListItem>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {childrenWithProps}
        </List>
      </Collapse>
    </Box>
  );
};

// Main Navigation Component
const NavigationList = ({ onItemClick, children }) => {
  return (
    <NavigationProvider onItemClick={onItemClick}>
      <ScrollableList>
        {children}
      </ScrollableList>

      <DrawerFooter>
        <VersionMenu />
      </DrawerFooter>
    </NavigationProvider>
  );
};

// Export components
export default NavigationList;
export { NavSection, NavItem, NavGroup };